import { useCallback, useRef, useState } from 'react';
import {
  ListAtsJobsQuery,
  useListAtsJobsLazyQuery
} from 'generated-graphql/hooks/apollo-hooks';
import { FormOption } from 'common/form/types/Form';
import { logger } from 'core/utils/logger';
import { extractAtsJobs } from 'modules/ats/utils/extractAtsJobs';

const JOB_SEARCH_PAGE_SIZE = 100;

type AtsJob = NonNullable<ListAtsJobsQuery['listAtsJobs']>['jobs'][number];

type UseAtsJobSearchProps = {
  organizationId: string;
};

const formatJobLocation = (job: AtsJob): string =>
  [job.locationCity, job.locationState, job.locationCountry]
    .filter(Boolean)
    .join(', ');

const buildJobSubLabel = (job: AtsJob): string => {
  const location = formatJobLocation(job);
  return location ? `${location}. ID ${job.atsJobId}` : `ID ${job.atsJobId}`;
};

const dedupeOptionsByValue = (options: FormOption[]): FormOption[] => {
  const seen = new Set<FormOption['value']>();
  return options.filter((option) => {
    if (seen.has(option.value)) return false;
    seen.add(option.value);
    return true;
  });
};

/**
 * Server-side search for the "add to job" picker. Instead of paginating every
 * ATS job up-front (each page a live Kombo/ATS round-trip), this fetches a
 * single small page filtered by the typed term via `listAtsJobs`' built-in
 * `nameContains` filter — one ATS call per search.
 *
 * `getJob` resolves a selected job's label and stages from the ids seen so far,
 * so it survives after the visible options change to a later search's results.
 */
export const useAtsJobSearch = ({ organizationId }: UseAtsJobSearchProps) => {
  const [fetchAtsJobs] = useListAtsJobsLazyQuery();
  // Caches each job's label and stage options by id as results stream in, so
  // `getJob` can still resolve a selected job after a later search replaces the
  // visible options. Stages ride along on the jobs query, so a selected job's
  // statuses/labels are available without a second ATS round-trip.
  const jobByIdRef = useRef<
    Map<string, { label: string; stageOptions: FormOption[] }>
  >(new Map());
  // Monotonic id so only the newest in-flight search updates `error` — a stale
  // failure must not resurface an error after a newer search has cleared it.
  const latestRequestIdRef = useRef(0);
  const [error, setError] = useState<Error | null>(null);

  const cacheJobAndBuildOption = useCallback((job: AtsJob): FormOption => {
    const label = job.title ?? job.atsJobReference ?? job.atsJobId;
    const stageOptions = (job.stages ?? []).map((stage) => ({
      label: stage.name ?? '',
      value: stage.komboStageId
    }));
    jobByIdRef.current.set(job.atsJobId, { label, stageOptions });
    return {
      label,
      // `atsJobId` (the ATS-native id) is populated for every provider,
      // including Bullhorn — unlike `atsJobReference` (Kombo `job_code`), which
      // Bullhorn leaves null. Show it so similar roles stay distinguishable.
      subLabel: buildJobSubLabel(job),
      value: job.atsJobId
    };
  }, []);

  const runQuery = useCallback(
    async (
      filter?: { nameContains: string } | { atsJobIds: string[] }
    ): Promise<FormOption[]> => {
      const { data, error: queryError } = await fetchAtsJobs({
        variables: {
          input: {
            organizationId,
            pageSize: JOB_SEARCH_PAGE_SIZE,
            ...(filter ? { filter } : {})
          }
        }
      });
      // `useLazyQuery`'s execute function resolves (rather than rejects) with
      // an `error` field on network/GraphQL errors, so it must be checked.
      if (queryError) {
        throw queryError;
      }
      return extractAtsJobs(data).map(cacheJobAndBuildOption);
    },
    [fetchAtsJobs, organizationId, cacheJobAndBuildOption]
  );

  const searchJobs = useCallback(
    async (jobTitleQueryText: string): Promise<FormOption[]> => {
      const requestId = (latestRequestIdRef.current += 1);
      const trimmed = jobTitleQueryText.trim();
      // ATS job ids never contain whitespace, so only a whitespace-free term
      // could be one. Kombo AND-combines top-level filters and has no id-
      // substring filter, so an exact id must be a separate `remote_ids` query.
      const couldBeJobId = trimmed !== '' && !/\s/.test(trimmed);
      try {
        const nameOptions = await runQuery(
          trimmed ? { nameContains: trimmed } : undefined
        );

        // Title search found nothing: the term might BE an exact id. This id
        // lookup is the only result source, so its failure must surface (via
        // the catch). Runs for any id-shaped term so id search works whatever
        // the provider's id format.
        if (couldBeJobId && nameOptions.length === 0) {
          const idOptions = await runQuery({ atsJobIds: [trimmed] });
          if (requestId === latestRequestIdRef.current) {
            setError(null);
          }
          return idOptions;
        }

        // The (primary) title search succeeded.
        if (requestId === latestRequestIdRef.current) {
          setError(null);
        }

        // Title results may be shadowing a *different* job whose exact id equals
        // the term (e.g. a numeric Bullhorn id like `3456` that also appears in
        // "Store 3456 Manager"). Only plausible when the term looks like an id
        // (contains a digit) and that job isn't already shown. This is a best-
        // effort top-up: a failure here must not discard the title results.
        const hasExactIdAlready = nameOptions.some(
          (option) => option.value === trimmed
        );
        if (couldBeJobId && /\d/.test(trimmed) && !hasExactIdAlready) {
          try {
            const idOptions = await runQuery({ atsJobIds: [trimmed] });
            return dedupeOptionsByValue([...nameOptions, ...idOptions]);
          } catch (idTopUpError) {
            logger.error(idTopUpError, {
              message: 'Failed exact ATS job id top-up; keeping title results',
              organizationId
            });
          }
        }

        return nameOptions;
      } catch (searchError) {
        logger.error(searchError, {
          message: 'Failed to search ATS jobs',
          organizationId
        });
        if (requestId === latestRequestIdRef.current) {
          setError(
            searchError instanceof Error
              ? searchError
              : new Error('Failed to search ATS jobs')
          );
        }
        return [];
      }
    },
    [runQuery, organizationId]
  );

  // Fetch a single job by its ATS (remote) id so a preselected job (e.g. the
  // one tied to an analysis) resolves its label and stages even when it isn't on
  // the picker's first page of results. Short-circuits if already cached.
  // Returns null if it can't be found/loaded.
  const fetchJobById = useCallback(
    async (atsJobId: string): Promise<FormOption | null> => {
      const cached = jobByIdRef.current.get(atsJobId);
      if (cached) {
        return { label: cached.label, value: atsJobId };
      }
      try {
        const { data, error: queryError } = await fetchAtsJobs({
          variables: {
            input: {
              organizationId,
              pageSize: 1,
              filter: { atsJobIds: [atsJobId] }
            }
          }
        });
        if (queryError) {
          throw queryError;
        }
        const [job] = extractAtsJobs(data);
        return job ? cacheJobAndBuildOption(job) : null;
      } catch (ensureError) {
        logger.error(ensureError, {
          message: 'Failed to load ATS job by id',
          organizationId,
          atsJobId
        });
        return null;
      }
    },
    [fetchAtsJobs, organizationId, cacheJobAndBuildOption]
  );

  // Resolves a cached job's label and stage options together, so a selected job
  // survives after the visible options change to a later search's results.
  const getJob = useCallback(
    (atsJobId: string) => jobByIdRef.current.get(atsJobId),
    []
  );

  return { searchJobs, fetchJobById, getJob, error };
};
